import { createSignal, onMount, onCleanup, createEffect, Show, For, createMemo } from "solid-js";

import { getData, getTimezoneForDate, setupMediaContainerScaling } from "../../../../utils/global";
import { persistantStore } from "../../../../store/persistantStore";
import { apiEndpoints } from "@config/env";
import { debug, error as logError } from "../../../../utils/console";
import { logPageLoad } from "../../../../utils/logging";
import Loading from "../../../../components/utilities/Loading";
import DropDownButton from "../../../../components/buttons/DropDownButton";
import { unifiedDataStore } from "../../../../store/unifiedDataStore";
import { resolveDataField } from "../../../../utils/colorScale";
import Violin from "../../../../components/charts/Violin";
import TimeSeriesSettings from "../../../../components/menus/TimeSeriesSettings";
import { sourcesStore } from "../../../../store/sourcesStore";
import {
  selectedSources as filterStoreSelectedSources,
  setSelectedSources as filterStoreSetSelectedSources,
} from "../../../../store/filterStore";
import { useDashboardShareRestore } from "../../../../hooks/useDashboardShareRestore";
import { buildCalibrationShareState, applyCalibrationShareState } from "../../../../utils/reportShareState";
import { user } from "../../../../store/userStore";
import { ensureValidSourceSelection } from "../../../../utils/sourceInitialization";
import { runWithConcurrency, FLEET_SOURCE_FETCH_CONCURRENCY } from "../../../../utils/concurrency";
import {
  FOILING_FILTER_OPTIONS,
  foilingFilterDescription,
  foilingFilterFromLabel,
  foilingFilterLabel,
  passesFoilingFilter,
  type FoilingFilter,
} from "../../../../utils/foilingStateFilter";

const { selectedClassName, selectedProjectId, selectedDate } = persistantStore;

/** Match dataset Calibration: upwind 30° < |TWA| < 75°, downwind 120° < |TWA| < 155°. */
const CALIBRATION_UPWIND_ABS_TWA_MIN = 30;
const CALIBRATION_UPWIND_ABS_TWA_MAX = 75;
const CALIBRATION_DOWNWIND_ABS_TWA_MIN = 120;
const CALIBRATION_DOWNWIND_ABS_TWA_MAX = 155;

const CALIBRATION_VIOLIN_GRADES: ReadonlySet<number> = new Set([2, 3]);

/** Exclude rows with BSP ≤ this value (same field/units as `resolveBspField`: kph preferred, else kts). */
const FLEET_CALIBRATION_MIN_BSP_EXCLUSIVE = 5;

const FLEET_CALIBRATION_CHANNELS = [
  "ts",
  "Datetime",
  "Grade",
  "Twa_deg",
  "Twa_cor_deg",
  "Twa_n_deg",
  "Twa_n_cor_deg",
  "Aws_cor_kph",
  "Aws_kph",
  "Aws_fused_norm_kph",
  "Aws_fused_kph",
  "Awa_n_cor_deg",
  "Tws_kts",
  "Tws_cor_kph",
  "Twd_cor_deg",
  "Bsp_kph",
  "Bsp_kts",
  // Ride-height split: the pipeline's per-row Foiling_state (0/1/2), with Heel_n_deg as the
  // fallback the derivation needs on a parquet that predates it.
  "Foiling_state",
  "Heel_n_deg",
  "Lwy_n_cor_deg",
] as const;

function getVal(d: Record<string, unknown>, key: string): number | null {
  const v = resolveDataField(d, key) ?? (d as Record<string, unknown>)[key];
  if (v === undefined || v === null || Number.isNaN(Number(v))) return null;
  return Number(v);
}

function calibrationRowGradeNum(d: Record<string, unknown>): number | null {
  const raw =
    (d as { Grade?: unknown }).Grade ??
    (d as { grade?: unknown }).grade ??
    (d as { GRADE?: unknown }).GRADE;
  if (raw === undefined || raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function isCalibrationViolinGradeRow(d: Record<string, unknown>): boolean {
  const g = calibrationRowGradeNum(d);
  return g !== null && CALIBRATION_VIOLIN_GRADES.has(g);
}

function isCalibrationUpwindTwa(twa: number): boolean {
  const a = Math.abs(twa);
  return a > CALIBRATION_UPWIND_ABS_TWA_MIN && a < CALIBRATION_UPWIND_ABS_TWA_MAX;
}

function isCalibrationDownwindTwa(twa: number): boolean {
  const a = Math.abs(twa);
  return a > CALIBRATION_DOWNWIND_ABS_TWA_MIN && a < CALIBRATION_DOWNWIND_ABS_TWA_MAX;
}

function passesSelectedWindSector(twa: number, wind: "upwind" | "downwind"): boolean {
  return wind === "upwind" ? isCalibrationUpwindTwa(twa) : isCalibrationDownwindTwa(twa);
}

function resolveTwaFieldForViolins(data: Record<string, unknown>[]): string {
  return data.some((d) => getVal(d, "Twa_cor_deg") !== null) ? "Twa_cor_deg" : "Twa_deg";
}

function resolveBspField(data: Record<string, unknown>[]): { field: string; yLabel: string } {
  if (data.some((d) => getVal(d, "Bsp_kph") !== null)) return { field: "Bsp_kph", yLabel: "BSP (kph)" };
  if (data.some((d) => getVal(d, "Bsp_kts") !== null)) return { field: "Bsp_kts", yLabel: "BSP (kts)" };
  return { field: "Bsp_kph", yLabel: "BSP (kph)" };
}

function resolveTwsField(data: Record<string, unknown>[]): { field: string; yLabel: string } {
  if (data.some((d) => getVal(d, "Tws_cor_kph") !== null)) return { field: "Tws_cor_kph", yLabel: "TWS (kph)" };
  if (data.some((d) => getVal(d, "Tws_kts") !== null)) return { field: "Tws_kts", yLabel: "TWS (kts)" };
  return { field: "Tws_kts", yLabel: "TWS (kts)" };
}

function rowSourceName(d: Record<string, unknown>): string {
  return String(
    resolveDataField(d, "SOURCE_NAME") ??
      resolveDataField(d, "source_name") ??
      (d as { SOURCE_NAME?: string }).SOURCE_NAME ??
      (d as { source_name?: string }).source_name ??
      ""
  ).trim();
}

function buildFleetViolinData(
  rows: Record<string, unknown>[],
  valueField: string,
  twaField: string,
  wind: "upwind" | "downwind",
  foiling: FoilingFilter,
  bspGate: { field: string; minExclusive: number }
): { SOURCE_NAME: string; Value: number; Twa_signed: number }[] {
  const out: { SOURCE_NAME: string; Value: number; Twa_signed: number }[] = [];
  for (const d of rows) {
    if (!isCalibrationViolinGradeRow(d)) continue;
    if (!passesFoilingFilter(d, foiling)) continue;
    const twa = getVal(d, twaField);
    if (twa === null || !passesSelectedWindSector(twa, wind)) continue;
    const bsp = getVal(d, bspGate.field);
    if (bsp === null || bsp <= bspGate.minExclusive) continue;
    const val = getVal(d, valueField);
    if (val === null) continue;
    const sn = rowSourceName(d);
    if (!sn) continue;
    out.push({ SOURCE_NAME: sn, Value: val, Twa_signed: twa });
  }
  return out;
}

interface SourceRow {
  source_id: number;
  source_name: string;
  dataset_id: number;
}

/** Per-source load result, so a failed fetch is distinguishable from a boat filtered out on purpose. */
interface SourceLoadOutcome {
  sourceName: string;
  rows: number;
  failed: boolean;
  message?: string;
}

/** How long to wait for the source catalog before resolving the selection (10 x 100ms, as in onMount). */
const SOURCES_READY_MAX_ATTEMPTS = 10;
const SOURCES_READY_POLL_MS = 100;

export default function FleetCalibrationPage() {
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);
  const [mergedData, setMergedData] = createSignal<Record<string, unknown>[]>([]);
  const [uwDw, setUwDw] = createSignal<"UPWIND" | "DOWNWIND">("UPWIND");
  const [foilingFilter, setFoilingFilter] = createSignal<FoilingFilter>("FOILING");
  const [loadOutcomes, setLoadOutcomes] = createSignal<SourceLoadOutcome[]>([]);

  /**
   * Request generation. The effect below re-fires while a load is still in flight (mount signals settle
   * at different times, and ensureValidSourceSelection writes selectedSources asynchronously). Without
   * this guard whichever fetch burst returned last won, so the boat list differed on every reload.
   * Same pattern as TrainingSummary.tsx.
   */
  let fetchSeq = 0;

  const FLEET_CALIBRATION_PAGE_KEY = 'FleetCalibration';
  useDashboardShareRestore(
    FLEET_CALIBRATION_PAGE_KEY,
    () =>
      buildCalibrationShareState({
        uwDw: uwDw(),
        foilingDisp: foilingFilterLabel(foilingFilter()),
        selectedSources: [...filterStoreSelectedSources()],
      }),
    (obj) =>
      applyCalibrationShareState(obj, FLEET_CALIBRATION_PAGE_KEY, {
        setUwDw,
        setFoilingDisp: (value: unknown) => setFoilingFilter(foilingFilterFromLabel(value)),
      })
  );

  const selectedSourceIdsSet = createMemo(() => {
    const names = filterStoreSelectedSources();
    if (!sourcesStore.isReady() || !Array.isArray(names)) return new Set<number>();
    const ids = new Set<number>();
    for (const name of names) {
      const n = String(name).trim().toLowerCase();
      const s = sourcesStore.sources().find((x) => String(x.source_name).trim().toLowerCase() === n);
      if (s && Number.isFinite(Number(s.source_id))) ids.add(Number(s.source_id));
    }
    return ids;
  });

  const dataSourcesOptions = createMemo(() => {
    if (!sourcesStore.isReady()) return [];
    const projectSources = sourcesStore.sources();
    if (!projectSources?.length) return [];
    return projectSources
      .map((s) => {
        const id = Number(s.source_id);
        if (!Number.isFinite(id)) return null;
        const name = s.source_name || "";
        const getter = () => {
          const names = filterStoreSelectedSources();
          return Array.isArray(names) && names.some((n) => String(n).trim().toLowerCase() === name.trim().toLowerCase());
        };
        const setter = (value: boolean) => {
          const current = filterStoreSelectedSources();
          const arr = Array.isArray(current) ? [...current] : [];
          const norm = name.trim().toLowerCase();
          const idx = arr.findIndex((n) => String(n).trim().toLowerCase() === norm);
          if (value) {
            if (idx === -1) arr.push(name);
          } else if (idx !== -1) {
            arr.splice(idx, 1);
          }
          filterStoreSetSelectedSources(arr);
        };
        return {
          key: `source-${id}`,
          label: name || `Source ${id}`,
          type: "toggle" as const,
          signal: [getter, setter] as [() => boolean, (value: boolean) => void],
        };
      })
      .filter((opt): opt is NonNullable<typeof opt> => opt !== null);
  });

  const metricViolins = createMemo(() => {
    const data = mergedData();
    const wind: "upwind" | "downwind" = uwDw() === "DOWNWIND" ? "downwind" : "upwind";
    const foiling = foilingFilter();
    if (!data.length) return [] as {
      title: string;
      data: { SOURCE_NAME: string; Value: number; Twa_signed: number }[];
      chart: {
        series: Array<{
          xaxis: { name: string };
          yaxis: { name: string; dataField: string };
          groupField: string;
          groups: { name: string; color: string }[];
        }>;
      };
    }[];

    const twaF = resolveTwaFieldForViolins(data);
    const bsp = resolveBspField(data);
    const tws = resolveTwsField(data);
    const bspGate = { field: bsp.field, minExclusive: FLEET_CALIBRATION_MIN_BSP_EXCLUSIVE };

    const sourceNames = [
      ...new Set(data.map((d) => rowSourceName(d)).filter(Boolean)),
    ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

    const groups = sourceNames.map((name) => ({
      name,
      color: sourcesStore.getSourceColor(name) || "#1f77b4",
    }));

    const rows: {
      title: string;
      data: { SOURCE_NAME: string; Value: number; Twa_signed: number }[];
      yLabel: string;
    }[] = [
      {
        title: "AWS",
        yLabel: "AWS (kph)",
        data: buildFleetViolinData(data, "Aws_cor_kph", twaF, wind, foiling, bspGate),
      },
      {
        title: "AWA",
        yLabel: "AWA (°)",
        data: buildFleetViolinData(data, "Awa_n_cor_deg", twaF, wind, foiling, bspGate),
      },
      {
        title: "TWS",
        yLabel: tws.yLabel,
        data: buildFleetViolinData(data, tws.field, twaF, wind, foiling, bspGate),
      },
      {
        title: "TWD",
        yLabel: "TWD (°)",
        data: buildFleetViolinData(data, "Twd_cor_deg", twaF, wind, foiling, bspGate),
      },
      {
        title: "TWA",
        yLabel: "TWA (°)",
        data: buildFleetViolinData(data, "Twa_n_cor_deg", twaF, wind, foiling, bspGate),
      },
      {
        title: "BSP",
        yLabel: bsp.yLabel,
        data: buildFleetViolinData(data, bsp.field, twaF, wind, foiling, bspGate),
      },
      {
        title: "LWY",
        yLabel: "LWY (°)",
        data: buildFleetViolinData(data, "Lwy_n_cor_deg", twaF, wind, foiling, bspGate),
      },
    ];

    return rows.map((r) => ({
      title: r.title,
      data: r.data,
      chart: {
        series: [
          {
            xaxis: { name: "Source" },
            yaxis: { name: r.yLabel, dataField: "Value" },
            groupField: "SOURCE_NAME",
            groups,
          },
        ],
      },
    }));
  });

  /**
   * Boats that are not on the charts, split by cause. A boat missing because its fetch failed or returned
   * nothing is a bug; a boat missing because no row cleared the Grade 2-3 / ride-height / TWA / BSP filters is
   * expected. Without this the two were indistinguishable - the boat was simply absent.
   */
  const missingBoats = createMemo(() => {
    const outcomes = loadOutcomes();
    if (outcomes.length === 0) return { noData: [], filteredOut: [] };

    const plotted = new Set<string>();
    for (const metric of metricViolins()) {
      for (const d of metric.data) plotted.add(d.SOURCE_NAME.trim().toLowerCase());
    }

    const noData: string[] = [];
    const filteredOut: string[] = [];
    for (const o of outcomes) {
      if (o.failed || o.rows === 0) {
        noData.push(o.sourceName);
      } else if (!plotted.has(o.sourceName.trim().toLowerCase())) {
        filteredOut.push(o.sourceName);
      }
    }
    return { noData, filteredOut };
  });

  async function loadCalibrationFleetData(): Promise<void> {
    const requestSeq = ++fetchSeq;
    /** True once a newer load has started; this one must not write to any signal after that. */
    const isStale = () => requestSeq !== fetchSeq;
    const finishIfCurrent = () => {
      if (!isStale()) setLoading(false);
    };

    setLoading(true);
    setError(null);
    try {
      const className = selectedClassName();
      const projectId = selectedProjectId();
      const date = selectedDate();
      if (!className || !projectId || !date) {
        if (isStale()) return;
        setError("Select a project and date.");
        setMergedData([]);
        setLoadOutcomes([]);
        return;
      }

      // Resolve the source selection BEFORE any await, and only once the catalog is loaded. Read after an
      // await, selectedSourceIdsSet() returned an empty set (= "all sources") whenever the store happened
      // to still be loading - so the same reload could show the full fleet or the saved subset at random.
      for (let attempt = 0; !sourcesStore.isReady() && attempt < SOURCES_READY_MAX_ATTEMPTS; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, SOURCES_READY_POLL_MS));
      }
      if (isStale()) return;
      const selectedIds = selectedSourceIdsSet();

      const dateStr = String(date);
      const dateNorm = dateStr.includes("-") ? dateStr.replace(/-/g, "") : dateStr;

      const timezone = await getTimezoneForDate(className, Number(projectId), dateStr);
      let url = `${apiEndpoints.app.datasets}/date/dataset_id?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(dateStr)}`;
      if (timezone) url += `&timezone=${encodeURIComponent(timezone)}`;

      const res = await getData(url);
      if (isStale()) return;
      if (!res?.success || !Array.isArray(res.data)) {
        setError("Failed to load sources for this date.");
        setMergedData([]);
        setLoadOutcomes([]);
        return;
      }

      const availableSources: SourceRow[] = [];
      const seen = new Set<number>();
      for (const row of res.data as Record<string, unknown>[]) {
        const sid = Number(row.source_id ?? row.sourceId);
        const sname = row.source_name ?? row.sourceName;
        const dsid = Number(row.dataset_id ?? row.datasetId);
        if (Number.isFinite(sid) && sname && !seen.has(sid)) {
          seen.add(sid);
          availableSources.push({ source_id: sid, source_name: String(sname), dataset_id: dsid });
        }
      }

      const sourcesToUse =
        selectedIds.size > 0 ? availableSources.filter((s) => selectedIds.has(s.source_id)) : availableSources;

      if (sourcesToUse.length === 0) {
        setError("No sources selected or available for this date.");
        setMergedData([]);
        setLoadOutcomes([]);
        return;
      }

      // Bounded fan-out: one request per boat all at once oversubscribes the server's heavy-query cap, and
      // the boats that lose that race are shed or time out and vanish from the page. Queue here instead.
      const perSource = await runWithConcurrency(
        sourcesToUse.map((s) => async (): Promise<{ rows: Record<string, unknown>[]; outcome: SourceLoadOutcome } | null> => {
          // Abandon a superseded load HERE, not just at the end: the guard below only stops a stale run
          // from writing its results, so without this check every queued boat still hit the API and three
          // overlapping loads tripled the request burst against a shared server cap.
          if (isStale()) return null;
          try {
            const rows = await unifiedDataStore.fetchDataWithChannelChecking(
              "ts",
              className,
              String(s.source_id),
              [...FLEET_CALIBRATION_CHANNELS],
              {
                projectId: String(projectId),
                className,
                datasetId: String(s.dataset_id),
                sourceName: s.source_name,
                date: dateNorm,
                timezone: timezone || undefined,
                applyGlobalFilters: false,
                use_v2: true,
                skipTimeRangeFilter: true,
              },
              "timeseries"
            );
            const arr = Array.isArray(rows) ? rows : [];
            debug("FleetCalibration: loaded source", { source: s.source_name, rows: arr.length });
            return {
              rows: arr.map((r) => ({
                ...(r as Record<string, unknown>),
                SOURCE_NAME: s.source_name,
                source_name: s.source_name,
              })),
              outcome: { sourceName: s.source_name, rows: arr.length, failed: false },
            };
          } catch (e) {
            logError("FleetCalibration: fetch failed for source", s.source_name, e);
            return {
              rows: [],
              outcome: {
                sourceName: s.source_name,
                rows: 0,
                failed: true,
                message: e instanceof Error ? e.message : String(e),
              },
            };
          }
        }),
        FLEET_SOURCE_FETCH_CONCURRENCY
      );

      if (isStale()) return;
      const loaded = perSource.filter((r): r is NonNullable<typeof r> => r !== null);
      const merged = loaded.flatMap((r) => r.rows);
      setLoadOutcomes(loaded.map((r) => r.outcome));
      setMergedData(merged);
      debug("FleetCalibration: merged total rows", merged.length);
    } catch (e) {
      logError("FleetCalibration: load error", e);
      if (isStale()) return;
      setError(e instanceof Error ? e.message : "Failed to load calibration data.");
      setMergedData([]);
      setLoadOutcomes([]);
    } finally {
      finishIfCurrent();
    }
  }

  onMount(() => {
    const cleanupScaling = setupMediaContainerScaling({
      logPrefix: "FleetCalibration",
      scaleToWidth: true,
    });
    onCleanup(cleanupScaling);
  });

  onMount(async () => {
    await logPageLoad("FleetCalibration.tsx", "Fleet Calibration");
    const u = user();
    const cn = selectedClassName();
    const pid = selectedProjectId();
    if (u?.user_id && cn && pid) {
      let attempts = 0;
      while (!sourcesStore.isReady() && attempts < 10) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }
      if (sourcesStore.isReady()) {
        try {
          const validSources = await ensureValidSourceSelection();
          debug("FleetCalibration: Ensured valid source selection", { count: validSources.length, sources: validSources });
        } catch (e) {
          debug("FleetCalibration: ensureValidSourceSelection skipped", e);
        }
      }
    }
  });

  createEffect(() => {
    selectedDate();
    selectedClassName();
    selectedProjectId();
    filterStoreSelectedSources();
    // Do not gate the effect on sourcesStore.isReady(): date/dataset_id drives the fetch, and the store is
    // only needed for filter name→id. loadCalibrationFleetData waits for the catalog itself before it
    // resolves the selection, so a slow store no longer silently turns the selection into "all sources".
    // Re-entry while a load is in flight is handled by the fetchSeq guard, not by skipping the run.
    void loadCalibrationFleetData();
  });

  return (
    <div id="media-container" class="fleet-calibration-page">
      <Show when={loading()}>
        <Loading message="Loading…" />
      </Show>
      <Show when={!loading() && error()}>
        <div class="calibration-error">
          <p>{error()}</p>
        </div>
      </Show>
      <Show when={!loading() && !error()}>
        <div class="fleet-calibration-header">
          <div class="fleet-calibration-header-bar">
            <div class="fleet-calibration-settings-slot">
              <TimeSeriesSettings
                isFleet={true}
                useIconTrigger={true}
                dataSourcesOptions={dataSourcesOptions()}
                filterConfig={{
                  showGrades: false,
                  showTWA: false,
                  showRaces: false,
                  showLegs: false,
                  showPhases: false,
                  showPeriods: false,
                  showBins: false,
                  showHeadsail: false,
                  showMainsail: false,
                  showConfiguration: false,
                }}
                raceOptions={[]}
                legOptions={[]}
                gradeOptions={[]}
                objectName="CALIBRATION"
              />
            </div>
            <DropDownButton
              options={["UPWIND", "DOWNWIND"]}
              defaultText={uwDw()}
              handleSelection={(value: string) => {
                if (value === "UPWIND" || value === "DOWNWIND") setUwDw(value);
              }}
              smallLabel="Uw/Dw"
              size="medium"
            />
            <DropDownButton
              options={[...FOILING_FILTER_OPTIONS]}
              defaultText={foilingFilterLabel(foilingFilter())}
              handleSelection={(value: string) => setFoilingFilter(foilingFilterFromLabel(value))}
              smallLabel="State"
              size="big"
            />
          </div>
          <div class="fleet-calibration-legend-note">{foilingFilterDescription(foilingFilter())}</div>
          <Show when={missingBoats().noData.length > 0 || missingBoats().filteredOut.length > 0}>
            <div class="fleet-calibration-legend-note">
              <Show when={missingBoats().noData.length > 0}>
                <div>Not loaded (no data returned): {missingBoats().noData.join(", ")}</div>
              </Show>
              <Show when={missingBoats().filteredOut.length > 0}>
                <div>Loaded but no rows passed the filters: {missingBoats().filteredOut.join(", ")}</div>
              </Show>
            </div>
          </Show>
        </div>

        <Show
          when={mergedData().length > 0}
          fallback={<p class="fleet-calibration-legend-note">No rows loaded for the selected sources.</p>}
        >
          <section class="fleet-calibration-violins">
            <For each={metricViolins()}>
              {(m) => (
                <div class="fleet-calibration-violin-row">
                  <div class="fleet-calibration-violin-inner">
                    <h3 class="fleet-calibration-metric-title">{m.title}</h3>
                    <div class="fleet-calibration-violin-container">
                      <Violin
                        chart={m.chart}
                        data={m.data}
                        twaField="Twa_signed"
                        portColor="#c00"
                        stbdColor="#2ca02c"
                        showOverallMeanLine={true}
                      />
                    </div>
                  </div>
                </div>
              )}
            </For>
          </section>
        </Show>
      </Show>
    </div>
  );
}
